import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid2,
  Container,
  Typography,
  Paper,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import handleAxiosError from "../../components/ErrorHandler";
import { notifErr, notifOk } from "../../components/Functions";
import { TrainingSchedule } from "../../model/entity/TrainingSchedule";
import {
  trainingScheduleValidationObjInit,
  validateTrainingSchedule,
} from "../../model/validation/TrainingScheduleValidation";
import { Loading } from "../../components/Loading";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import {
  deleteFetcher,
  getFetcher,
  putFetcher,
} from "../../components/Fetchers";
import LoadingDialog from "../../components/LoadingDialog";

const EditSchedule = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [schedule, setSchedule] = useState<TrainingSchedule | null>(null);
  const [scheduleErr, setScheduleErr] = useState(
    trainingScheduleValidationObjInit
  );
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getFetcher(
          `${process.env.REACT_APP_API_URL}/schedules/${id}`
        );
        setSchedule(response.data);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (!schedule) return;
    if (triedSubmit) setScheduleErr(validateTrainingSchedule(schedule));
  }, [schedule, triedSubmit]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!schedule) return;
    setSchedule({ ...schedule, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schedule) return;

    setTriedSubmit(true);

    const scheduleErrors = validateTrainingSchedule(schedule);
    setScheduleErr(scheduleErrors);

    if (Object.values(scheduleErrors).some((val) => val === false)) return;

    try {
      await putFetcher(
        `${process.env.REACT_APP_API_URL}/schedules/${id}`,
        schedule
      );
      notifOk("Rozvrh úspěšně vytvořen.");
      navigate("/schedule/settings");
    } catch (err) {
      notifErr(handleAxiosError(err));
    }
  };

  const handleCancel = () => {
    navigate("/schedule/settings");
  };

  const handleDeleteConfirm = async () => {
    if (!schedule) return;
    try {
      setProcessing(true);
      await deleteFetcher(
        `${process.env.REACT_APP_API_URL}/schedules/${schedule.id}`
      );
      notifOk("Rozvrh úspěšně odstraněn.");
      navigate("/schedule/settings");
    } catch (err) {
      notifErr(handleAxiosError(err));
    } finally {
      setOpenDialog(false);
      setProcessing(false);
    }
  };

  if (schedule === null) return <Loading />;

  const confirmationDialogMsg = (
    <div>
      Opravdu chcete trvale smazat rozvrh
      <b> {schedule.name}</b>?<br />
      Tato akce je nevratná.
    </div>
  );

  const loadingDialogTitle = "Probíhá mazání rozvrhu...";
  const loadingDialogMsg =
    "Mazání komplexního rozvrhu může trvat i několik desítek sekund.";

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pb: "2rem",
      }}
    >
      <LoadingDialog
        open={processing}
        title={loadingDialogTitle}
        msg={loadingDialogMsg}
      />
      <ConfirmationDialog
        open={openDialog}
        setOpen={setOpenDialog}
        msg={confirmationDialogMsg}
        handleSubmit={handleDeleteConfirm}
      />
      <Paper elevation={3} sx={{ padding: "1.5rem" }}>
        <Typography variant="h5" sx={{ marginBottom: "1.5rem" }}>
          {schedule.name}
        </Typography>
        <Grid2 container spacing={3} size={{ xs: 12, md: 6 }}>
          <Grid2 size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Název"
              required
              name="name"
              value={schedule.name}
              error={!scheduleErr.name}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD.MM.YYYY"
                label="Datum zahájení"
                sx={{ width: "100%" }}
                name="startDate"
                value={
                  schedule.startDate === "" ? null : dayjs(schedule.startDate)
                }
                onChange={(date) =>
                  setSchedule({
                    ...schedule,
                    startDate: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    error:
                      !scheduleErr.startDate &&
                      !dayjs(schedule.startDate, "YYYY-MM-DD", true).isValid(),
                  },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD.MM.YYYY"
                label="Datum ukončení"
                sx={{ width: "100%" }}
                name="endDate"
                value={schedule.endDate === "" ? null : dayjs(schedule.endDate)}
                onChange={(date) =>
                  setSchedule({
                    ...schedule,
                    endDate: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    error:
                      !scheduleErr.endDate &&
                      !dayjs(schedule.endDate, "YYYY-MM-DD", true).isValid(),
                  },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>
          </Grid2>

          <Grid2
            container
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
            id="top"
          >
            <Grid2 id="middle" size={{ xs: 12, md: 6 }}>
              <Button
                className="deleteBtn"
                variant="contained"
                onClick={() => setOpenDialog(true)}
              >
                Smazat
              </Button>
            </Grid2>
            <Grid2
              id="bottom"
              size={{ xs: 12, md: 6 }}
              sx={{ display: "flex", justifyContent: "right", gap: 2 }}
            >
              <Button
                className="cancelBtn"
                variant="outlined"
                onClick={handleCancel}
              >
                Zrušit
              </Button>
              <Button
                className="submitBtn"
                variant="outlined"
                onClick={handleSubmit}
              >
                Uložit
              </Button>
            </Grid2>
          </Grid2>
        </Grid2>
      </Paper>
    </Container>
  );
};

export default EditSchedule;
